import { supabase } from '../lib/supabase';

class ProjectDashboardService {
  // Get project progress overview
  async getProjectProgress(projectId) {
    try {
      const { data, error } = await supabase?.from('project_progress')?.select('*')?.eq('project_id', projectId)?.maybeSingle();

      if (error) throw error;
      
      return {
        inPipeline: data?.in_pipeline || 0,
        started: data?.started || 0,
        onFloor: data?.on_floor || 0,
        completed: data?.completed || 0,
        delayed: data?.delayed || 0,
        delayReasons: data?.delay_reasons || '',
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching project progress:', error);
      return null;
    }
  }

  // Get project modules (formerly products)
  async getProjectModules(projectId) {
    try {
      const { data, error } = await supabase?.from('project_products')?.select('*')?.eq('project_id', projectId)?.order('display_order', { ascending: true })?.order('product_name', { ascending: true });

      if (error) throw error;
      
      return data?.map(module => ({
        id: module?.id,
        sku: module?.sku,
        name: module?.product_name,
        description: module?.description,
        category: module?.category,
        areaUse: module?.area_use,
        quantity: module?.quantity,
        allocated: module?.allocated,
        completed: module?.completed,
        status: module?.status,
        productionValueUsd: module?.production_value_usd,
        dimensionLength: module?.dimension_length,
        dimensionWidth: module?.dimension_width,
        dimensionHeight: module?.dimension_height,
        weight: module?.weight,
        dryWetUnit: module?.dry_wet_unit,
        wallSystems: module?.wall_systems,
        fenestration: module?.fenestration,
        cladding: module?.cladding,
        displayOrder: module?.display_order,
        createdAt: module?.created_at,
        updatedAt: module?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching project modules:', error);
      return [];
    }
  }

  // Create new module
  async createModule(moduleData) {
    try {
      const { data, error } = await supabase?.from('project_products')?.insert([{
          project_id: moduleData?.projectId,
          sku: moduleData?.sku,
          product_name: moduleData?.productName,
          description: moduleData?.description,
          category: moduleData?.category,
          area_use: moduleData?.areaUse,
          quantity: moduleData?.quantity,
          allocated: moduleData?.allocated || 0,
          completed: moduleData?.completed || 0,
          status: moduleData?.status || 'pending',
          production_value_usd: moduleData?.productionValueUsd,
          dimension_length: moduleData?.dimensionLength,
          dimension_width: moduleData?.dimensionWidth,
          dimension_height: moduleData?.dimensionHeight,
          weight: moduleData?.weight,
          dry_wet_unit: moduleData?.dryWetUnit,
          wall_systems: moduleData?.wallSystems || false,
          fenestration: moduleData?.fenestration || false,
          cladding: moduleData?.cladding || false
        }])?.select()?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating module:', error);
      return { data: null, error: error?.message };
    }
  }

  // Update existing module
  async updateModule(moduleData) {
    try {
      const { data, error } = await supabase?.from('project_products')?.update({
          sku: moduleData?.sku,
          product_name: moduleData?.productName,
          description: moduleData?.description,
          category: moduleData?.category,
          area_use: moduleData?.areaUse,
          quantity: moduleData?.quantity,
          production_value_usd: moduleData?.productionValueUsd,
          dimension_length: moduleData?.dimensionLength,
          dimension_width: moduleData?.dimensionWidth,
          dimension_height: moduleData?.dimensionHeight,
          weight: moduleData?.weight,
          dry_wet_unit: moduleData?.dryWetUnit,
          wall_systems: moduleData?.wallSystems || false,
          fenestration: moduleData?.fenestration || false,
          cladding: moduleData?.cladding || false
        })?.eq('id', moduleData?.id)?.select()?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating module:', error);
      return { data: null, error: error?.message };
    }
  }

  // Delete module
  async deleteModule(moduleId) {
    try {
      const { data, error } = await supabase?.from('project_products')?.delete()?.eq('id', moduleId)?.select()?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error deleting module:', error);
      return { data: null, error: error?.message };
    }
  }

  // Bulk update modules
  async bulkUpdateModules(moduleIds, updates) {
    try {
      const updateData = {};
      if (updates?.category) updateData.category = updates?.category;
      if (updates?.areaUse) updateData.area_use = updates?.areaUse;
      if (updates?.status) updateData.status = updates?.status;

      const { data, error } = await supabase?.from('project_products')?.update(updateData)?.in('id', moduleIds)?.select();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error bulk updating modules:', error);
      return { data: null, error: error?.message };
    }
  }

  // Bulk approve modules (set status to approved/in-progress)
  async bulkApproveModules(moduleIds) {
    try {
      const { data, error } = await supabase?.from('project_products')?.update({ status: 'in-progress' })?.in('id', moduleIds)?.select();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error bulk approving modules:', error);
      return { data: null, error: error?.message };
    }
  }

  // Bulk schedule modules (set status to pending with scheduled flag)
  async bulkScheduleModules(moduleIds) {
    try {
      const { data, error } = await supabase?.from('project_products')?.update({ status: 'pending' })?.in('id', moduleIds)?.select();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error bulk scheduling modules:', error);
      return { data: null, error: error?.message };
    }
  }

  // Update module display order
  async updateModuleOrder(moduleId, displayOrder) {
    try {
      const { data, error } = await supabase?.from('project_products')?.update({ display_order: displayOrder })?.eq('id', moduleId)?.select()?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating module order:', error);
      return { data: null, error: error?.message };
    }
  }

  // Bulk update module display orders
  async bulkUpdateModuleOrder(updates) {
    try {
      // updates is an array of { id, displayOrder }
      const promises = updates?.map(update => 
        supabase?.from('project_products')?.update({ display_order: update?.displayOrder })?.eq('id', update?.id)
      );

      const results = await Promise.all(promises);
      const errors = results?.filter(r => r?.error);

      if (errors?.length > 0) {
        throw new Error(`Failed to update ${errors?.length} module(s)`);
      }

      return { data: true, error: null };
    } catch (error) {
      console.error('Error bulk updating module order:', error);
      return { data: null, error: error?.message };
    }
  }

  // Get project resources
  async getProjectResources(projectId) {
    try {
      const { data, error } = await supabase?.from('project_resources')?.select('*')?.eq('project_id', projectId)?.order('resource_type', { ascending: true });

      if (error) throw error;
      
      return data?.map(resource => ({
        id: resource?.id,
        resourceType: resource?.resource_type,
        resourceName: resource?.resource_name,
        plannedQuantity: resource?.planned_quantity,
        actualQuantity: resource?.actual_quantity,
        unit: resource?.unit,
        utilizationPercentage: resource?.utilization_percentage,
        status: resource?.status,
        createdAt: resource?.created_at,
        updatedAt: resource?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching project resources:', error);
      return [];
    }
  }

  // Get QA/QC summary
  async getProjectQAQC(projectId) {
    try {
      const { data, error } = await supabase?.from('project_qaqc')?.select('*')?.eq('project_id', projectId)?.maybeSingle();

      if (error) throw error;
      
      return {
        totalInspections: data?.total_inspections || 0,
        passed: data?.passed || 0,
        failed: data?.failed || 0,
        pending: data?.pending || 0,
        passRate: data?.pass_rate || 0,
        criticalIssues: data?.critical_issues || 0,
        lastInspectionDate: data?.last_inspection_date,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching project QA/QC:', error);
      return null;
    }
  }

  // Get HSE summary
  async getProjectHSE(projectId) {
    try {
      const { data, error } = await supabase?.from('project_hse')?.select('*')?.eq('project_id', projectId)?.maybeSingle();

      if (error) throw error;
      
      return {
        safetyIncidents: data?.safety_incidents || 0,
        nearMisses: data?.near_misses || 0,
        safetyTrainingCompleted: data?.safety_training_completed || 0,
        safetyComplianceRate: data?.safety_compliance_rate || 0,
        daysWithoutIncident: data?.days_without_incident || 0,
        environmentalViolations: data?.environmental_violations || 0,
        lastSafetyAudit: data?.last_safety_audit,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching project HSE:', error);
      return null;
    }
  }

  // Get budget summary
  async getProjectBudget(projectId) {
    try {
      const { data, error } = await supabase?.from('project_budget')?.select('*')?.eq('project_id', projectId)?.maybeSingle();

      if (error) throw error;
      
      return {
        totalBudget: data?.total_budget || 0,
        allocatedBudget: data?.allocated_budget || 0,
        spentBudget: data?.spent_budget || 0,
        remainingBudget: data?.remaining_budget || 0,
        budgetVariance: data?.budget_variance || 0,
        variancePercentage: data?.variance_percentage || 0,
        currency: data?.currency || 'USD',
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching project budget:', error);
      return null;
    }
  }

  // Get cash flow summary
  async getProjectCashFlow(projectId) {
    try {
      const { data, error } = await supabase?.from('project_cashflow')?.select('*')?.eq('project_id', projectId)?.order('month', { ascending: true });

      if (error) throw error;
      
      return data?.map(cashflow => ({
        id: cashflow?.id,
        month: cashflow?.month,
        inflow: cashflow?.inflow || 0,
        outflow: cashflow?.outflow || 0,
        netCashflow: cashflow?.net_cashflow || 0,
        cumulativeCashflow: cashflow?.cumulative_cashflow || 0,
        createdAt: cashflow?.created_at,
        updatedAt: cashflow?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching project cash flow:', error);
      return [];
    }
  }

  // Get risk predictions
  async getProjectRisks(projectId) {
    try {
      const { data, error } = await supabase?.from('project_risks')?.select('*')?.eq('project_id', projectId)?.order('risk_score', { ascending: false });

      if (error) throw error;
      
      return data?.map(risk => ({
        id: risk?.id,
        riskCategory: risk?.risk_category,
        riskDescription: risk?.risk_description,
        probability: risk?.probability,
        impact: risk?.impact,
        riskScore: risk?.risk_score,
        mitigationPlan: risk?.mitigation_plan,
        status: risk?.status,
        createdAt: risk?.created_at,
        updatedAt: risk?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching project risks:', error);
      return [];
    }
  }

  // Get all dashboard data at once
  async getAllDashboardData(projectId) {
    try {
      const [progress, modules, resources, qaqc, hse, budget, cashflow, risks] = await Promise.all([
        this.getProjectProgress(projectId),
        this.getProjectModules(projectId),
        this.getProjectResources(projectId),
        this.getProjectQAQC(projectId),
        this.getProjectHSE(projectId),
        this.getProjectBudget(projectId),
        this.getProjectCashFlow(projectId),
        this.getProjectRisks(projectId)
      ]);

      return {
        progress,
        modules,
        resources,
        qaqc,
        hse,
        budget,
        cashflow,
        risks
      };
    } catch (error) {
      console.error('Error fetching all dashboard data:', error);
      return null;
    }
  }
}

export default new ProjectDashboardService();